import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import HeroSystemCard from "@/components/HeroSystemCard";

const scrollTo = (id: string) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const microcopy =
  "Pour les entreprises de services qui veulent une croissance structurée, prévisible et mesurable.";

const HeroV2 = () => (
  <section id="accueil" aria-labelledby="hero-heading" className="relative overflow-hidden pt-[88px] pb-12 md:pt-32 md:pb-20">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_20%_10%,hsl(var(--primary)/0.10)_0%,transparent_70%)]" />
    </div>

    <div className="relative z-10 mx-auto flex max-w-[1280px] flex-col gap-8 px-5 md:px-8 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-14">
      <div>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-medium uppercase tracking-[0.22em] text-primary md:text-xs"
        >
          Agence créative + partenaire de croissance
        </motion.p>

        <motion.h1
          id="hero-heading"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          className="mt-3.5 max-w-2xl font-heading text-[2rem] sm:text-5xl font-semibold leading-[1.08] tracking-tight text-foreground md:mt-5 md:leading-[1.03] lg:text-6xl"
        >
          Votre marketing devrait générer des clients.
          <span className="block text-muted-foreground">Pas seulement du contenu.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.6 }}
          className="mt-4 max-w-[42ch] text-[0.95rem] leading-[1.55] text-muted-foreground md:mt-6 md:max-w-xl md:text-lg"
        >
          Agence 80/20 connecte la stratégie, la création, la publicité et le suivi dans un système de croissance
          construit autour de vos objectifs.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24, duration: 0.6 }}
          className="mt-7 flex flex-col gap-2.5 sm:flex-row sm:items-center md:mt-8 md:gap-3"
        >
          <Button
            size="lg"
            onClick={() => {
              trackEvent("primary_cta_clicked", { location: "hero" });
              scrollTo("#formulaire");
            }}
            className="group w-full rounded-full bg-primary px-7 py-5 text-base font-semibold text-primary-foreground hover:bg-primary/90 sm:w-auto md:py-6"
          >
            Nous parler de votre entreprise
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
          <Button
            size="lg"
            variant="ghost"
            onClick={() => {
              trackEvent("portfolio_viewed", { location: "hero" });
              scrollTo("#realisations");
            }}
            className="w-full rounded-full px-7 py-5 text-base font-medium text-muted-foreground hover:bg-secondary hover:text-foreground sm:w-auto border border-border text-foreground md:py-6"
          >
            Voir nos réalisations
          </Button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-6 max-w-lg text-sm leading-relaxed text-muted-foreground/80"
        >
          {microcopy}
        </motion.p>
      </div>

      {/* Carte animée « système » : contenu -> publicité -> résultats */}
      <HeroSystemCard />

    </div>
  </section>
);

export default HeroV2;
